import { Button, DatePicker, Form, Input, InputNumber, Modal, Select, Table, message } from 'antd';
import { useEffect, useState } from 'react';
import { CourseTime } from '../../components/CourseTime';
import { EmptyState } from '../../components/EmptyState';
import { StatusBadge } from '../../components/StatusBadge';
import { newClientToken } from '../../lib/client-token';
import { courseStatusLabel } from '../../lib/status';
import { coursesApi, labsApi, periodsApi } from '../../services/resources';
import type { Course, Lab, Period } from '../../types/api';

export function AdminCoursesPage() {
  const [items, setItems] = useState<Course[]>([]);
  const [labs, setLabs] = useState<Lab[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    try {
      const [c, l, p] = await Promise.all([
        coursesApi.list({ page: 1, limit: 50 }),
        labsApi.list({ page: 1, limit: 50 }),
        periodsApi.list(),
      ]);
      setItems(c.items);
      setLabs(l.items);
      setPeriods(p);
      setError('');
    } catch {
      setError('课程列表失败');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function publish(row: Course) {
    Modal.confirm({
      title: '发布开放课',
      content: '发布后该房间对应节次立即占用，报名截止不释放。',
      onOk: async () => {
        await coursesApi.publish(row.id, newClientToken());
        await load();
      },
    });
  }

  return (
    <div className="stack">
      <h1 className="page-title">开放实验</h1>
      <Button type="primary" className="touch" onClick={() => setOpen(true)}>
        发布开放课
      </Button>
      {error ? (
        <EmptyState text={error} />
      ) : items.length === 0 ? (
        <EmptyState text="本周暂无开放实验课。" />
      ) : (
        <Table
          scroll={{ x: 780 }}
          rowKey="id"
          dataSource={items}
          pagination={false}
          expandable={{
            expandedRowRender: (c) => <CourseTime course={c} />,
          }}
          columns={[
            { title: '课名', dataIndex: 'title' },
            { title: '开课日期', dataIndex: 'course_date' },
            {
              title: '状态',
              render: (_, r) => <StatusBadge status={r.status} label={courseStatusLabel(r.status)} />,
            },
            { title: '容量', dataIndex: 'capacity' },
            { title: '已报', dataIndex: 'enrolled_count' },
            {
              title: '操作',
              render: (_, r) =>
                r.status === 'draft' ? (
                  <Button className="touch" type="primary" onClick={() => void publish(r)}>
                    发布
                  </Button>
                ) : null,
            },
          ]}
        />
      )}
      <CreateCourseModal
        open={open}
        labs={labs}
        periods={periods}
        onClose={() => setOpen(false)}
        onOk={() => void load()}
      />
    </div>
  );
}

function CreateCourseModal(props: {
  open: boolean;
  labs: Lab[];
  periods: Period[];
  onClose: () => void;
  onOk: () => void;
}) {
  const [form] = Form.useForm();
  return (
    <Modal
      title="新建开放课（草稿）"
      open={props.open}
      onCancel={props.onClose}
      onOk={async () => {
        const v = await form.validateFields();
        await coursesApi.create({
          title: v.title,
          lab_id: v.lab_id,
          course_date: v.course_date.format('YYYY-MM-DD'),
          period_ids: v.period_ids,
          signup_open_at: v.signup[0].toISOString(),
          signup_close_at: v.signup[1].toISOString(),
          capacity: v.capacity,
        });
        message.success('已保存草稿');
        props.onOk();
        props.onClose();
      }}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="title" label="课名" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="lab_id" label="实验室" rules={[{ required: true }]}>
          <Select options={props.labs.map((l) => ({ value: l.id, label: `${l.room_code} ${l.name}` }))} />
        </Form.Item>
        <Form.Item name="course_date" label="开课日期" rules={[{ required: true }]}>
          <DatePicker />
        </Form.Item>
        <Form.Item name="period_ids" label="节次" rules={[{ required: true }]}>
          <Select mode="multiple" options={props.periods.map((p) => ({ value: p.id, label: p.name }))} />
        </Form.Item>
        <Form.Item name="signup" label="报名开始 / 报名截止" rules={[{ required: true }]}>
          <DatePicker.RangePicker showTime />
        </Form.Item>
        <Form.Item name="capacity" label="容量" rules={[{ required: true }]}>
          <InputNumber min={1} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
